import { customFetch } from '@agos/api-client'
import type { Branding } from '@agos/api-client/model'
import { useEffect, useState } from 'react'

/**
 * Per-franchise white-label branding, ported from legacy `lib/use-branding.ts`.
 *
 * Resolved by the backend from the request `Host` header, so a franchise on its
 * own domain gets its own name, logo, favicon and colours before anybody signs
 * in. On localhost no domain matches and the backend falls back to the first
 * active franchise, which is what makes the login screen look real in
 * development.
 */
// The shape used to be hand-written here, and it was two fields short of what the endpoint
// answers — `themeName` and `modules` were never declared. It comes from the backend now
// (BLOCKED #4); re-exported because callers import `Branding` from this module.
export type { Branding }

/**
 * The name this franchise goes by, from `/central/branding` - `FRONTEND.md` §9.700.
 *
 * The display name, else the registry name, else empty - the server's `FranchiseName` in the browser. **Empty
 * rather than a default, deliberately**: the screens that used to fall back named one franchise for every
 * other (*PizzaMajstro* in Sereď's sidebar, on its printed trip log, in its e-mail previews), and each caller
 * decides what to say when even this is silent. Takes `unknown` so a screen can hand it a generated hook's
 * answer without a cast.
 */
export function franchiseNameOf(branding: unknown): string {
  if (branding === null || typeof branding !== 'object') return ''
  const row = branding as { displayName?: unknown; name?: unknown }
  const display = typeof row.displayName === 'string' ? row.displayName.trim() : ''
  if (display !== '') return display
  return typeof row.name === 'string' ? row.name.trim() : ''
}

/**
 * The name a printed page or the sidebar puts on this franchise - `FRONTEND.md` §9.700, extracted at §9.783.
 *
 * The name the franchise typed in *Dizajn*, else what the platform calls it. Written out by the sidebar and the
 * trip log before the cashback sheet needed it too; a third copy of the rule is how one of them comes to print
 * another restaurant's name, so it is spelled here once.
 */
export function restaurantNameOf(designedName: string, branding: unknown): string {
  const designed = designedName.trim()
  return designed !== '' ? designed : franchiseNameOf(branding)
}

function applyBranding(branding: Branding): void {
  const root = document.documentElement

  if (branding.primaryColor) root.style.setProperty('--brand-primary', branding.primaryColor)
  if (branding.secondaryColor) root.style.setProperty('--brand-secondary', branding.secondaryColor)
  if (branding.displayName) document.title = branding.displayName

  if (branding.faviconUrl) {
    const existing = document.querySelector<HTMLLinkElement>("link[rel='icon']")
    const link = existing ?? document.createElement('link')
    link.rel = 'icon'
    link.href = branding.faviconUrl
    if (!existing) document.head.appendChild(link)
  }

  // Free-form overrides, e.g. { "--accent": "#ff0" }. Only custom properties
  // are applied — a key that is not one would be a stored value writing
  // arbitrary CSS.
  if (branding.theme && typeof branding.theme === 'object') {
    for (const [property, value] of Object.entries(branding.theme)) {
      if (typeof value === 'string' && property.startsWith('--')) {
        root.style.setProperty(property, value)
      }
    }
  }
}

export function useBranding(): { branding: Branding | null; isLoading: boolean } {
  const [branding, setBranding] = useState<Branding | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        const resolved = await customFetch<Branding>('/api/v1/central/branding')
        if (cancelled) return
        setBranding(resolved)
        applyBranding(resolved)
      } catch {
        // No branding is survivable: the app falls back to its own name.
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [])

  return { branding, isLoading }
}
